import * as React from 'react';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import { setExpanded } from '../../actions/actions';
import { Html5Entities } from 'html-entities';
import PropTypes from 'prop-types';

import ieClosest from '../../polyfills/closest';
import { isSelected, isExpanded } from '../../helpers/ui';
import ChildCount from './ChildCount';
import './styles/Node.scss';

const htmlEntities = new Html5Entities();

export class NodeClass extends React.Component {
	constructor(props, context) {
		super(props, context)
	}

	toggleChildren = (e, showChildren) => {
		// Toggle display of children
		e.stopPropagation();

		const { keyVal, expanded, toggleExpansion } = this.props;
		toggleExpansion(keyVal, expanded, !showChildren);
	}

	openChildren = () => {
		const { keyVal, expanded, toggleExpansion } = this.props;
		toggleExpansion(keyVal, expanded, true);
	}

	shouldComponentUpdate(nextProps, _nextState) {
		// Only update this node if we've updated the checkbox or toggled the display of the children
		const { keyVal, selectedList, expanded, node: { value: { id } } } = this.props;

		// Compare expanded list from global state to see if this node was expanded or closed
		const childrenToggled = isExpanded({keyVal, expanded}) !== isExpanded({keyVal, expanded: nextProps.expanded});

		// Compare selected account list to see if this account was added or removed
		const checkboxToggled = isSelected({acctId: id, selectedList}) !== isSelected({acctId: id, selectedList: nextProps.selectedList});

		return childrenToggled || checkboxToggled;
	}

	render() {
		// polyfill for closest
		ieClosest();

		let childNodes;
		const { keyVal, isRootNode, addRemoveItem, i18n, selectedList, expanded, checkForEmptyList } = this.props;
		const { children, value: { id, name, descendantAccountIds, franchiseList } } = this.props.node;
		const showChildren = isExpanded({keyVal, expanded});

		// The Node component calls itself to generate its children
		// Child Nodes are only added if they are visible, to improve performance with large account trees
		if (showChildren && children && Array.isArray(children)) {
			childNodes = children.map(node => {
				const { value: { id, descendantAccountIds }, children } = node;
				return (
					<Node
						node={node}
						key={`${keyVal}-${id}`}
						keyVal={`${keyVal}-${id}`}
						allDescendants={descendantAccountIds}
						children={children} // eslint-disable-line react/no-children-prop
						addRemoveItem={addRemoveItem}
						checkForEmptyList={checkForEmptyList}
						selectedList={selectedList}
						i18n={i18n}
					/>
				)
			});
		}

		// An item is checked if the account is in the list of selected accounts
		const isChecked = isSelected({acctId: id, selectedList});
		const hasChildren = children && children.length > 0;

		// Return our list element, including children if expanded
		// The root node is always expanded
		return (
			<li className={`${showChildren ? 'open' : 'closed'} ${isRootNode ? 'root' : ''}`} >
				<span
					className={`node ${hasChildren ? 'parent' : ''}`}
					onClick={hasChildren && !isRootNode ? e => this.toggleChildren(e, showChildren) : null}
				>
					<div className={`checkbox ${isChecked ? 'checked' : ''}`}
						data-acct-id={id}
						onClick={e =>{
							// Toggle checkbox
							e.stopPropagation();
							checkForEmptyList([id], !isChecked);
							addRemoveItem([id], !isChecked);
						}}>
						<span></span>
					</div>
					<span className='dealer-name'>{htmlEntities.decode(name)} {String.raw`(${htmlEntities.decode(id)})`}</span>
					{/* <span className='franchise-list'>{franchiseList.join(', ')}</span> */}
					{hasChildren ? <ChildCount descendantAccountIds={descendantAccountIds} /> : null }
					<div hidden={!hasChildren}>
						<Button
							bsStyle="link"
							bsSize="small"
							onClick={e => {
								e.stopPropagation();
								this.openChildren();
								addRemoveItem(descendantAccountIds, true);
							}}
						>
							{i18n.t('SELECT_ALL')}
						</Button>
						<Button
							bsStyle="link"
							bsSize="small"
							onClick={e => {
								e.stopPropagation();
								checkForEmptyList(descendantAccountIds, false);
								addRemoveItem(descendantAccountIds, false);
							}}
						>
							{i18n.t('DESELECT_ALL')}
						</Button>
					</div>
				</span>
				{childNodes ? <ul>{childNodes}</ul> : null }
			</li>
		);
	}
}

NodeClass.propTypes = {
	keyVal: PropTypes.string.isRequired,
	addRemoveItem: PropTypes.func.isRequired,
	checkForEmptyList: PropTypes.func.isRequired,
	toggleExpansion: PropTypes.func.isRequired,
	children: PropTypes.array.isRequired,
	i18n: PropTypes.shape({}).isRequired,
	isRootNode: PropTypes.bool,
	node: PropTypes.shape({
		children: PropTypes.array.isRequired,
		value: PropTypes.shape({}).isRequired,
	}).isRequired,
	selectedList: PropTypes.shape({}),
	treeIsLoaded: PropTypes.bool,
	expanded: PropTypes.array,
};

NodeClass.defaultProps = {
	isRootNode: false,
	selectedList: {},
	treeIsLoaded: false,
	expanded: [],
}

const mapStateToProps = (state) => ({
	allLists: state.allLists || [],
	selectedList: state.selectedList,
	treeIsLoaded: state.treeIsLoaded,
	expanded: state.expanded,
});

const mapDispatchToProps = dispatch => ({
	toggleExpansion: (keyVal, expanded, show) => {
		if (show) {
			// show children
			dispatch(setExpanded([...expanded, keyVal]));
		} else {
			// hide children
			dispatch(setExpanded([...expanded].filter(x => x !== keyVal)));
		}
	},
});
const Node = connect(
	mapStateToProps,
	mapDispatchToProps
)(NodeClass);

export default (Node);